/**
 * @page ProzessMobilePage
 * @route /p/:token (public, no auth)
 * @description Magic-Link-Landing fuer Prozess-Notifications (#918).
 *   Mobile-optimiert. Kein Login, Token reicht. Acknowledge-Button.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, FileText, Clock } from 'lucide-react';
import ProcessHtmlView from '../components/ProcessHtmlView';

const API = import.meta.env.VITE_API_URL || '';

const EVENT_LABELS = {
  'prozess.aktiv_neu': 'Neuer Prozess aktiv',
  'prozess.major_freigabe': 'Prozess-Update freigegeben',
  'prozess.entsperrt_review': 'Prozess entsperrt – Review nötig',
};

export default function ProzessMobilePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [err, setErr] = useState('');
  const [acking, setAcking] = useState(false);

  async function load() {
    try {
      const res = await fetch(`${API}/api/public/notifications/m/${token}`);
      if (res.status === 404) { setStatus('invalid'); return; }
      if (res.status === 410) { setStatus('expired'); return; }
      if (!res.ok) { setStatus('error'); return; }
      const d = await res.json();
      setData(d);
      setStatus(d.acknowledged_at ? 'acknowledged' : 'ready');
    } catch (e) {
      setErr(e.message);
      setStatus('error');
    }
  }

  useEffect(() => { load(); }, [token]);

  async function ack() {
    setAcking(true);
    try {
      const res = await fetch(`${API}/api/public/notifications/m/${token}/acknowledge`, { method: 'POST' });
      if (res.ok) {
        setStatus('acknowledged');
        await load();
      } else {
        const d = await res.json().catch(() => ({}));
        setErr(d.error || 'acknowledge_failed');
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setAcking(false);
    }
  }

  if (status === 'loading') {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC] text-[#6B7280]">Lade …</div>;
  }
  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="w-12 h-12 text-rose-500 mb-3" strokeWidth={1.5} />
        <h1 className="text-xl font-semibold text-[#1A1A2E]">Link unbekannt</h1>
        <p className="text-sm text-[#6B7280] mt-2">Dieser Link existiert nicht oder wurde widerrufen.</p>
      </div>
    );
  }
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex flex-col items-center justify-center p-6 text-center">
        <Clock className="w-12 h-12 text-amber-500 mb-3" strokeWidth={1.5} />
        <h1 className="text-xl font-semibold text-[#1A1A2E]">Link abgelaufen</h1>
        <p className="text-sm text-[#6B7280] mt-2">Bitte wende dich an deine Teamleitung für einen neuen Link.</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="w-12 h-12 text-rose-500 mb-3" strokeWidth={1.5} />
        <h1 className="text-xl font-semibold text-[#1A1A2E]">Fehler</h1>
        <p className="text-sm text-[#6B7280] mt-2">{err || 'Bitte später nochmal versuchen.'}</p>
      </div>
    );
  }

  const eventLabel = EVENT_LABELS[data.event_type] || data.event_type;
  const acked = !!data.acknowledged_at;

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      <div className="max-w-xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="bg-[#338edc] text-white rounded-card p-5 mb-4">
          <div className="text-xs uppercase tracking-wider opacity-90 mb-1">{eventLabel}</div>
          <h1 className="text-2xl font-semibold leading-tight">{data.prozess.titel}</h1>
          {data.prozess.version && (
            <div className="text-sm opacity-90 mt-1">Version {data.prozess.version}</div>
          )}
        </div>

        {/* Begrüßung */}
        <div className="bg-white border border-[#E5E7EB] rounded-card p-4 mb-4">
          <div className="flex items-center gap-2 text-sm text-[#4B5563]">
            <span>Hallo {data.mitarbeiter.vorname},</span>
          </div>
          <p className="text-sm text-[#4B5563] mt-2">
            für dich liegt eine neue Prozess-Information vor. Bitte unten bestätigen, sobald gelesen.
          </p>
        </div>

        {/* Inhalt */}
        {data.prozess.inhalt && (
          <div className="bg-white border border-[#E5E7EB] rounded-card p-4 mb-4">
            <div className="flex items-center gap-2 text-sm font-medium text-[#1A1A2E] mb-2">
              <FileText className="w-4 h-4" strokeWidth={1.5} />
              Prozess-Inhalt
            </div>
            <ProcessHtmlView html={data.prozess.inhalt} className="text-sm" />
          </div>
        )}

        {/* Acknowledge */}
        {acked ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-card p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="text-sm font-medium text-emerald-900">Bestätigt</div>
              <div className="text-xs text-emerald-700 mt-0.5">
                {new Date(data.acknowledged_at).toLocaleString('de-AT')}
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={ack}
            disabled={acking}
            className="w-full bg-[#338edc] hover:bg-[#2a76b8] text-white font-semibold rounded-card py-3 transition-colors disabled:opacity-50"
          >
            {acking ? 'Bestätige …' : 'Gelesen & verstanden'}
          </button>
        )}

        {err && (
          <div className="text-sm text-rose-600 mt-3 text-center">{err}</div>
        )}

        <div className="text-xs text-[#9CA3AF] text-center mt-6">
          WeShip · QM-Modul
        </div>
      </div>
    </div>
  );
}
